import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // null = still checking, false = unauthenticated, object = authenticated
  const [user, setUser] = useState(null);
  const [account, setAccount] = useState(null);

  // Admin "view as" context — which store's data to show across every tab.
  // "all" = aggregate across all stores; an account id = impersonate that store.
  // Persisted so it survives tab navigation and refreshes.
  const [viewAs, setViewAsState] = useState(() => localStorage.getItem("bh_view_as") || "all");
  const [viewAsName, setViewAsName] = useState(() => localStorage.getItem("bh_view_as_name") || "");

  const setViewAs = useCallback((id, name = "") => {
    const v = id || "all";
    setViewAsState(v);
    setViewAsName(name);
    localStorage.setItem("bh_view_as", v);
    localStorage.setItem("bh_view_as_name", name);
  }, []);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setAccount(data.account);
      return data;
    } catch {
      setUser(false);
      setAccount(null);
      return null;
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    try {
      await api.post("/auth/login", { email, password });
      await fetchMe();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  }, [fetchMe]);

  const register = useCallback(async (payload) => {
    try {
      await api.post("/auth/register", payload);
      await fetchMe();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e) };
    }
  }, [fetchMe]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Backend logout is idempotent; we still clear local state.
      if (process.env.NODE_ENV !== "production") {
        console.warn("Logout request failed; clearing local session anyway.", e?.message || e);
      }
    }
    setUser(false);
    setAccount(null);
  }, []);

  const can = useCallback((module, level = "view") => {
    if (!user) return false;
    // Permissions are computed server-side for every role (including admins:
    // super admins get everything; regular employees get everything except
    // whatever a super admin has switched off, e.g. Billing).
    const p = user.permissions?.[module];
    if (!p) return false;
    return level === "manage" ? !!p.manage : !!(p.view || p.manage);
  }, [user]);

  // Stable context value — prevents unnecessary re-renders of consumers.
  const value = useMemo(
    () => ({
      user,
      account,
      isAdmin: user?.role === "admin",
      isSuperAdmin: user?.role === "admin" && !!user?.super_admin,
      isStaff: user?.role === "admin" || user?.role === "returns_manager",
      isReturnsManager: user?.role === "returns_manager",
      isOwner: user?.role === "owner",
      role: user?.role,
      permissions: user?.permissions || null,
      canManageTeam: !!user?.can_manage_team,
      can,
      viewAs,
      viewAsName,
      setViewAs,
      login,
      register,
      logout,
      refresh: fetchMe,
    }),
    [user, account, can, viewAs, viewAsName, setViewAs, login, register, logout, fetchMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
